import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { trackEvent } from "@/lib/analytics";
import { GRANT_PROGRAMS, type GrantProgram } from "./grantProgramData";

interface Props {
  group: GrantProgram["group"];
  entrySection: string;
}

export default function GrantProgramCards({ group, entrySection }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const programs = GRANT_PROGRAMS.filter((p) => p.group === group);

  const toggle = (p: GrantProgram) => {
    const next = open === p.id ? null : p.id;
    setOpen(next);
    if (next) {
      trackEvent("program_card_expand", {
        entry_section: entrySection,
        program_id: p.id,
        assistance_type: p.assistance_type,
        repayment_type: p.repayment_type,
      });
    }
  };

  return (
    <div className="grid gap-4">
      {programs.map((p) => {
        const isOpen = open === p.id;
        return (
          <Card key={p.id}>
            <CardContent className="p-0">
              <button
                type="button"
                onClick={() => toggle(p)}
                aria-expanded={isOpen}
                className="flex w-full items-start gap-3 p-5 text-left"
              >
                <ChevronDown
                  className={`mt-1 h-4 w-4 shrink-0 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden
                />
                <span className="flex-1">
                  <span className="block text-base font-bold text-foreground">{p.name}</span>
                  <span className="block text-sm text-muted-foreground">{p.provider}</span>
                  <span className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {p.assistance_type}
                    </span>
                    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                      {p.geography_scope}
                    </span>
                  </span>
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-border px-5 py-4">
                  <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{p.detail}</p>
                  <dl className="grid gap-3 sm:grid-cols-2">
                    <Field label="Repayment" value={p.repayment_type} />
                    <Field label="Income basis" value={p.income_basis} />
                    <Field label="First-time rule" value={p.first_time_rule} />
                    <Field label="Education required" value={p.education_required} />
                    <Field label="Geography" value={p.geography_scope} />
                    <Field label="Source last checked" value={p.source_last_checked} />
                  </dl>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wide text-foreground">{label}</dt>
      <dd className="text-sm leading-relaxed text-muted-foreground">{value}</dd>
    </div>
  );
}